import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  signal,
  untracked,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { SkeletonModule } from 'primeng/skeleton';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ClientSessionService } from '@brighthub/uisession';
import { CopilotContextComposerService } from '@brighthub/copilot';
import { ClientAiInsightsStateService } from './client-ai-insights-state.service';
import {
  AiInsight,
  AiInsightSeverity,
  FeedbackReason,
  FeedbackSentiment,
  InsightSource,
} from './client-ai-insights.model';

const SOURCE_ICONS: Record<InsightSource, string> = {
  email:  'pi pi-envelope',
  sms:    'pi pi-comment',
  call:   'pi pi-phone',
  chat:   'pi pi-comments',
  manual: 'pi pi-pencil',
  agent:  'pi pi-microchip-ai',
};

const SOURCE_LABELS: Record<InsightSource, string> = {
  email:  'Email',
  sms:    'SMS',
  call:   'Call',
  chat:   'Chat',
  manual: 'Manual',
  agent:  'Agent',
};

const FEEDBACK_REASONS_HELPFUL: { value: FeedbackReason; label: string }[] = [
  { value: 'accurate',   label: 'Accurate' },
  { value: 'actionable', label: 'Actionable' },
  { value: 'timely',     label: 'Timely' },
];

const FEEDBACK_REASONS_NOT_HELPFUL: { value: FeedbackReason; label: string }[] = [
  { value: 'inaccurate', label: 'Inaccurate' },
  { value: 'irrelevant', label: 'Irrelevant' },
  { value: 'outdated',   label: 'Outdated' },
  { value: 'other',      label: 'Other' },
];

@Component({
  selector: 'bai-client-ai-insights',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, DatePipe, SkeletonModule, TagModule, TooltipModule],
  templateUrl: './client-ai-insights.component.html',
  styleUrl: './client-ai-insights.component.scss',
})
export class ClientAiInsightsComponent implements OnDestroy {
  private readonly _session = inject(ClientSessionService);
  readonly insightsState = inject(ClientAiInsightsStateService);
  private readonly _copilotComposer = inject(CopilotContextComposerService);

  // ---------------------------------------------------------------------------
  // Session-keyed state
  // ---------------------------------------------------------------------------

  private readonly _sessionId = computed(() => this._session.selectedSession()?.uiSessionId ?? null);

  readonly state = computed(() => {
    const id = this._sessionId();
    if (!id) return null;
    return this.insightsState.getState(id);
  });

  readonly insights = computed(() => this.state()?.insights ?? []);
  readonly unseenCount = computed(() => {
    const id = this._sessionId();
    return id ? this.insightsState.unseenCount(id) : 0;
  });
  readonly transientInsightIds = signal<Set<string>>(new Set<string>());
  private readonly _transientInsightTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly _lastStoryRevisionBySession = new Map<string, number>();
  private readonly _lastAiInsightIdsBySession = new Map<string, Set<string>>();

  // ---------------------------------------------------------------------------
  // Filter / search
  // ---------------------------------------------------------------------------

  readonly searchQuery = signal('');
  readonly filterType = signal<'all' | 'ai' | 'manual'>('all');

  readonly filteredInsights = computed(() => {
    const q = this.searchQuery().toLowerCase().trim();
    const type = this.filterType();
    return this.insights().filter((ins) => {
      if (type !== 'all' && ins.type !== type) return false;
      if (q && !ins.title.toLowerCase().includes(q) && !ins.body.toLowerCase().includes(q)) return false;
      return true;
    });
  });

  // ---------------------------------------------------------------------------
  // Feedback modal state
  // ---------------------------------------------------------------------------

  readonly feedbackModalOpen = signal(false);
  readonly feedbackInsight = signal<AiInsight | null>(null);
  readonly feedbackSentiment = signal<FeedbackSentiment>('helpful');
  readonly feedbackReason = signal<FeedbackReason | null>(null);
  readonly feedbackComment = signal('');

  readonly feedbackReasons = computed(() =>
    this.feedbackSentiment() === 'helpful'
      ? FEEDBACK_REASONS_HELPFUL
      : FEEDBACK_REASONS_NOT_HELPFUL
  );

  readonly feedbackCommentRequired = computed(() => this.feedbackSentiment() === 'notHelpful');

  readonly feedbackCanSubmit = computed(() => {
    if (this.feedbackCommentRequired() && !this.feedbackComment().trim()) return false;
    return true;
  });

  // ---------------------------------------------------------------------------
  // Add note modal state
  // ---------------------------------------------------------------------------

  readonly noteModalOpen = signal(false);
  readonly noteTitle = signal('');
  readonly noteBody = signal('');
  readonly noteTimestamp = signal(new Date().toISOString().slice(0, 16));

  readonly noteCanSubmit = computed(() => this.noteBody().trim().length > 0);

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  constructor() {
    effect(() => {
      const id = this._sessionId();
      if (id) {
        untracked(() => this.insightsState.ensureState(id));
      }
    });

    effect(() => {
      const id = this._sessionId();
      if (!id) return;

      const revision = this.insightsState.storyRevision(id);
      const previousRevision = this._lastStoryRevisionBySession.get(id);

      if (previousRevision === undefined) {
        this.clearTransientInsightLoading();
        this._lastStoryRevisionBySession.set(id, revision);
        this._lastAiInsightIdsBySession.set(id, this.currentAiInsightIds());
        untracked(() => this.insightsState.markAllSeen(id));
        return;
      }

      if (revision !== previousRevision) {
        const previousAiIds = this._lastAiInsightIdsBySession.get(id) ?? new Set<string>();
        const nextAiIds = this.currentAiInsightIds();
        const newlyAddedIds = [...nextAiIds].filter((insightId) => !previousAiIds.has(insightId));

        this._lastStoryRevisionBySession.set(id, revision);
        this._lastAiInsightIdsBySession.set(id, nextAiIds);
        untracked(() => this.insightsState.markAllSeen(id));
        if (newlyAddedIds.length > 0) {
          this.startTransientInsightLoading(newlyAddedIds);
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.clearTransientInsightLoading();
  }

  // ---------------------------------------------------------------------------
  // Helpers exposed to template
  // ---------------------------------------------------------------------------

  sourceIcon(source: InsightSource): string {
    return SOURCE_ICONS[source] ?? 'pi pi-info-circle';
  }

  sourceLabel(source: InsightSource): string {
    return SOURCE_LABELS[source] ?? source;
  }

  confidencePct(confidence: number | null): string {
    if (confidence === null) return '—';
    return `${Math.round(confidence * 100)}%`;
  }

  severityClass(severity: string): string {
    const map: Record<string, string> = {
      danger:  'bai-insight-card--danger',
      warn:    'bai-insight-card--warn',
      success: 'bai-insight-card--success',
      info:    'bai-insight-card--info',
    };
    return map[severity] ?? 'bai-insight-card--info';
  }

  severityTagSeverity(severity: AiInsightSeverity | string): 'danger' | 'warn' | 'success' | 'info' | 'secondary' {
    const map: Record<string, 'danger' | 'warn' | 'success' | 'info' | 'secondary'> = {
      danger:  'danger',
      warn:    'warn',
      success: 'success',
      info:    'info',
    };
    return map[severity] ?? 'secondary';
  }

  timeSavedLabel(minutes: number): string {
    if (minutes < 60) return `${minutes} min`;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }

  // ---------------------------------------------------------------------------
  // Feedback actions
  // ---------------------------------------------------------------------------

  openFeedback(insight: AiInsight, sentiment: FeedbackSentiment): void {
    this.feedbackInsight.set(insight);
    this.feedbackSentiment.set(sentiment);
    this.feedbackReason.set(null);
    this.feedbackComment.set('');
    this.feedbackModalOpen.set(true);
  }

  closeFeedbackModal(): void {
    this.feedbackModalOpen.set(false);
    this.feedbackInsight.set(null);
  }

  submitFeedback(): void {
    const id = this._sessionId();
    const insight = this.feedbackInsight();
    if (!id || !insight || !this.feedbackCanSubmit()) return;
    this.insightsState.submitFeedback(
      id,
      insight.id,
      this.feedbackSentiment(),
      this.feedbackReason(),
      this.feedbackComment(),
    );
    this.closeFeedbackModal();
  }

  // ---------------------------------------------------------------------------
  // Add note actions
  // ---------------------------------------------------------------------------

  openNoteModal(): void {
    this.noteTitle.set('');
    this.noteBody.set('');
    this.noteTimestamp.set(new Date().toISOString().slice(0, 16));
    this.noteModalOpen.set(true);
  }

  closeNoteModal(): void {
    this.noteModalOpen.set(false);
  }

  submitNote(): void {
    const id = this._sessionId();
    if (!id || !this.noteCanSubmit()) return;
    const ts = this.noteTimestamp()
      ? new Date(this.noteTimestamp()).toISOString()
      : new Date().toISOString();
    this.insightsState.addManualNote(id, this.noteTitle(), this.noteBody(), ts);
    this.closeNoteModal();
  }

  addInsightToCopilot(insight: AiInsight): void {
    const preview = this.truncate(insight.title, 20);
    this._copilotComposer.addContext({
      sourceType: 'ai-insights',
      sourceId: insight.id,
      label: `AI Insight: ${preview}`,
      fullText:
        `[AI Insight | ${insight.id}] ` +
        `Title: ${insight.title}; ` +
        `Body: ${insight.body}; ` +
        `Severity: ${insight.severity}; ` +
        `Source: ${this.sourceLabel(insight.source)}; ` +
        `Created: ${insight.createdAt}`,
    });
  }

  truncate(value: string, max = 20): string {
    if (value.length <= max) return value;
    return `${value.slice(0, max)}…`;
  }

  isTransientInsight(insightId: string): boolean {
    return this.transientInsightIds().has(insightId);
  }

  private currentAiInsightIds(): Set<string> {
    const currentState = this.state();
    if (!currentState) return new Set<string>();
    return new Set(
      currentState.insights
        .filter((insight) => insight.type === 'ai')
        .map((insight) => insight.id)
    );
  }

  private startTransientInsightLoading(insightIds: string[]): void {
    const nextIds = new Set(this.transientInsightIds());
    for (const insightId of insightIds) {
      const previousTimer = this._transientInsightTimers.get(insightId);
      if (previousTimer) {
        clearTimeout(previousTimer);
      }
      nextIds.add(insightId);
      const timer = setTimeout(() => {
        this._transientInsightTimers.delete(insightId);
        const remainingIds = new Set(this.transientInsightIds());
        remainingIds.delete(insightId);
        this.transientInsightIds.set(remainingIds);
      }, 200);
      this._transientInsightTimers.set(insightId, timer);
    }
    this.transientInsightIds.set(nextIds);
  }

  private clearTransientInsightLoading(): void {
    for (const timer of this._transientInsightTimers.values()) {
      clearTimeout(timer);
    }
    this._transientInsightTimers.clear();
    this.transientInsightIds.set(new Set<string>());
  }
}
